import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import SearchForm from './SearchForm';
import KeywordTable from './KeywordTable';
import QuickLookup from './QuickLookup';
import QuickLookupResult from './QuickLookupResult';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { subscribeToSearch } from '../channels/search_channel';

function Dashboard({
  searches,
  currentSearch,
  keywords,
  showingAllKeywords,
  allKeywords,
  pagination,
  showingQuickLookup,
  quickLookupProps,
  showingQuickLookupResult,
  quickLookupResultProps,
  currentUser,
}) {
  const [showNewSearch, setShowNewSearch] = useState(false);
  const [viewMode, setViewMode] = useState(
    showingAllKeywords ? 'all' : currentSearch ? 'search' : 'searches'
  );
  const [selectedSearch, setSelectedSearch] = useState(currentSearch);
  const [currentSearchStatus, setCurrentSearchStatus] = useState(
    currentSearch?.status
  );
  const [statusMessage, setStatusMessage] = useState('');
  const [progressData, setProgressData] = useState(null);

  // Subscribe to current search updates for real-time status changes
  useEffect(() => {
    if (
      currentSearch &&
      (currentSearch.status === 'pending' || currentSearch.status === 'running')
    ) {
      const subscription = subscribeToSearch(currentSearch.id, {
        onCompleted: (data) => {
          // Clear progress data and navigate to the completed search
          setProgressData(null);
          console.log('Search completed:', data);
          if (data?.redirect_url) {
            console.log('Redirecting to:', data.redirect_url);
            // Force a fresh load of the search page with results
            window.location.replace(data.redirect_url);
            console.log('Redirected to:', data.redirect_url);
          } else {
            console.log('Reloading search page');
            window.location.reload();
            console.log('Search page reloaded');
          }
        },
        onFailed: () => {
          // Clear progress data and reload to show failed status
          setProgressData(null);
          window.location.reload();
        },
        onUpdate: (data) => {
          // Update status in real-time
          console.log('Search status update:', data);
          setCurrentSearchStatus(data.status);
          setStatusMessage(data.message || '');

          // Handle completion in onUpdate since onCompleted isn't being called
          if (data.status === 'completed') {
            setProgressData(null);
            if (data.redirect_url) {
              console.log('Search completed, redirecting to:', data.redirect_url);
              window.location.replace(data.redirect_url);
              return;
            } else {
              console.log('Search completed, reloading page');
              window.location.reload();
              return;
            }
          }

          // Update progress data if available
          if (data.progress) {
            // Map backend data structure to frontend expectations
            const mappedProgress = {
              current_step: data.progress.step,
              total_steps: data.progress.total_steps,
              step_name: data.progress.substep || `Step ${data.progress.step}`,
              overall_progress: Math.round(
                (data.progress.step / data.progress.total_steps) * 100
              ),
              step_progress: data.progress.step_progress,
              processed_count: data.progress.processed_count,
              total_count: data.progress.total_count,
              total_harvested: data.progress.stats?.harvested,
              total_analyzed: data.progress.stats?.ai_analyzed,
              total_processed: data.progress.stats?.with_volume,
            };
            setProgressData(mappedProgress);
          }
        },
      });

      return () => {
        subscription?.unsubscribe();
      };
    }
  }, [currentSearch]);

  const handleNewSearch = async (formData) => {
    try {
      const response = await fetch('/searches', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify({
          search: {
            name: formData.name,
            seeds: formData.seeds,
            settings: formData.settings,
          },
        }),
      });

      if (response.ok) {
        const result = await response.json();
        // Redirect to the new search immediately (like before)
        window.location.href = `/searches/${result.id}`;
      } else {
        const errorText = await response.text();
        console.error('Failed to create search:', errorText);
        alert('Failed to create search. Please try again.');
      }
    } catch (error) {
      console.error('Error creating search:', error);
      alert('Error creating search. Please check your connection.');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-slate-700/50 text-slate-300 border border-slate-600/50',
      running: 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30',
      completed: 'bg-green-500/20 text-green-300 border border-green-500/30',
      failed: 'bg-red-500/20 text-red-300 border border-red-500/30',
    };
    return colors[status] || colors.pending;
  };

  const deleteSearch = async (searchId, searchName, keywordsCount) => {
    if (
      !confirm(
        `Are you sure you want to delete "${searchName}"? This will delete all ${keywordsCount} keywords from this search.`
      )
    ) {
      return;
    }

    try {
      const response = await fetch(`/searches/${searchId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // Reload the page to reflect the deletion
          window.location.reload();
        } else {
          console.error('Failed to delete search:', result.error);
        }
      } else {
        console.error('Failed to delete search:', response.statusText);
      }
    } catch (error) {
      console.error('Error deleting search:', error);
    }
  };

  const handleLogout = async () => {
    try {
      const response = await fetch('/logout', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
      });
      // Redirect will be handled by the server
      if (response.redirected) {
        window.location.href = response.url;
      } else {
        window.location.href = '/login';
      }
    } catch (error) {
      console.error('Logout failed:', error);
      window.location.href = '/login';
    }
  };

  const renderUserMenu = () => (
    <div className="flex items-center space-x-3">
      {currentUser && (
        <>
          <div className="flex items-center space-x-3 bg-slate-800/50 rounded-lg px-3 py-2 border border-slate-700/50">
            {currentUser.avatar_url && (
              <img
                src={currentUser.avatar_url}
                alt={currentUser.name}
                className="w-6 h-6 rounded-full"
              />
            )}
            <span className="text-sm text-gray-300">{currentUser.name}</span>
          </div>
          <button
            onClick={handleLogout}
            className="p-2 rounded-lg transition-all duration-200 text-gray-400 hover:text-red-400 hover:bg-red-500/10 border border-slate-700/50 hover:border-red-500/30"
            title="Logout"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
          </button>
        </>
      )}
    </div>
  );

  const renderSearchesOverview = () => (
    <div className="bg-slate-800/30 backdrop-blur-sm rounded-xl border border-slate-700/50 shadow-2xl">
      <div className="p-6 border-b border-slate-700/50">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-lg flex items-center justify-center">
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-white">
              Search Archives
            </h3>
            <p className="text-gray-400 mt-1">
              Access your keyword intelligence searches
            </p>
          </div>
        </div>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {searches?.map((search) => (
            <div
              key={search.id}
              onClick={() => {
                window.location.href = `/searches/${search.id}`;
              }}
              className="group p-5 bg-slate-800/50 border border-slate-700/50 rounded-xl hover:border-cyan-500/50 hover:bg-slate-800/70 cursor-pointer transition-all duration-200 hover:shadow-lg hover:shadow-cyan-500/10"
            >
              <div className="flex justify-between items-start mb-3">
                <h4 className="font-medium text-white truncate group-hover:text-cyan-300 transition-colors">
                  {search.name}
                </h4>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                    search.status
                  )}`}
                >
                  {search.status}
                </span>
              </div>
              <div className="text-sm text-gray-400 mb-3 flex items-center space-x-2">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>{formatDate(search.created_at)}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-300 flex items-center space-x-2">
                  <svg
                    className="w-4 h-4 text-cyan-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                  <span className="font-medium">{search.keywords_count}</span>
                  <span className="text-gray-400">targets</span>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteSearch(
                        search.id,
                        search.name,
                        search.keywords_count
                      );
                    }}
                    className="p-1.5 rounded-md transition-all duration-200 text-gray-400 hover:text-red-400 hover:bg-red-500/10 opacity-0 group-hover:opacity-100"
                    title="Delete search"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                  <svg
                    className="w-4 h-4 text-gray-400 group-hover:text-cyan-400 transition-colors"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>

        {(!searches || searches.length === 0) && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <MagnifyingGlassIcon className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">
              No Searches Detected
            </h3>
            <p className="text-gray-400 mb-6 max-w-md mx-auto">
              Initialize your first keyword intelligence scan to begin data
              collection
            </p>
            <button
              onClick={() => setShowNewSearch(true)}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 shadow-lg shadow-purple-500/25"
            >
              <span className="flex items-center space-x-2">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                <span>Initialize Search</span>
              </span>
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black">
      {/* Cyber grid background */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#1f2937_1px,transparent_1px),linear-gradient(to_bottom,#1f2937_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-20"></div>

      <div className="relative max-w-[140rem] mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 flex items-center justify-center">
                <img
                  src="/icon.svg"
                  alt="SEO Intelligence Hub Logo"
                  className="w-10 h-10"
                />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                  SEO Intelligence Hub
                </h1>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <p className="text-gray-400 text-sm font-medium">
                    Advanced keyword analysis powered by AI
                  </p>
                </div>
              </div>
            </div>
            {renderUserMenu()}
          </div>
        </div>

        {/* Navigation */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            {/* Main Navigation Tabs */}
            <div className="flex space-x-0.5 bg-slate-800/50 backdrop-blur-sm p-0.5 rounded-lg border border-slate-700/50">
              <button
                onClick={() => {
                  window.location.href = '/keywords';
                }}
                className={`px-3 py-2 rounded-md text-xs font-medium transition-all duration-200 ${
                  showingAllKeywords &&
                  new URLSearchParams(window.location.search).get(
                    'favorites'
                  ) !== 'true'
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg shadow-cyan-500/25'
                    : 'text-gray-300 hover:text-white hover:bg-slate-700/50'
                }`}
              >
                <span className="flex items-center space-x-1.5">
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                    />
                  </svg>
                  <span>Keywords</span>
                </span>
              </button>
              <button
                onClick={() => {
                  window.location.href = '/keywords?favorites=true';
                }}
                className={`px-3 py-2 rounded-md text-xs font-medium transition-all duration-200 ${
                  showingAllKeywords &&
                  new URLSearchParams(window.location.search).get(
                    'favorites'
                  ) === 'true'
                    ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-lg shadow-pink-500/25'
                    : 'text-gray-300 hover:text-white hover:bg-slate-700/50'
                }`}
              >
                <span className="flex items-center space-x-1.5">
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                    />
                  </svg>
                  <span>Favorites</span>
                </span>
              </button>
              <button
                onClick={() => {
                  window.location.href = '/';
                }}
                className={`px-3 py-2 rounded-md text-xs font-medium transition-all duration-200 ${
                  !showingAllKeywords &&
                  !showingQuickLookup &&
                  !showingQuickLookupResult &&
                  !currentSearch
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg shadow-cyan-500/25'
                    : 'text-gray-300 hover:text-white hover:bg-slate-700/50'
                }`}
              >
                <span className="flex items-center space-x-1.5">
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                    />
                  </svg>
                  <span>Searches</span>
                </span>
              </button>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-3">
              <button
                onClick={() => {
                  window.location.href = '/quick-lookup';
                }}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 border border-cyan-400/20"
              >
                <span className="flex items-center space-x-1.5">
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                  <span>Quick Lookup</span>
                </span>
              </button>
              <button
                onClick={() => setShowNewSearch(!showNewSearch)}
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 border border-purple-400/20"
              >
                <span className="flex items-center space-x-1.5">
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  <span>New Search</span>
                </span>
              </button>
            </div>
          </div>

          {/* Search selector for individual search view */}
          {currentSearch && (
            <div className="flex items-center space-x-4 bg-slate-800/30 backdrop-blur-sm p-4 rounded-xl border border-slate-700/50">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
                <span className="text-sm text-gray-400">Active scan:</span>
                <span className="font-medium text-white">
                  {currentSearch.name}
                </span>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                    currentSearchStatus || currentSearch.status
                  )}`}
                >
                  {currentSearchStatus || currentSearch.status}
                </span>
              </div>
              <div className="flex items-center space-x-3">
                {currentSearch.debug_logs_url && (
                  <button
                    onClick={() => {
                      window.open(currentSearch.debug_logs_url, '_blank');
                    }}
                    className="px-3 py-1 bg-yellow-500/20 border border-yellow-500/30 rounded-lg text-xs font-medium text-yellow-300 hover:bg-yellow-500/30 transition-colors"
                  >
                    <span className="flex items-center space-x-1.5">
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <span>Debug Logs</span>
                    </span>
                  </button>
                )}
                <button
                  onClick={() => {
                    window.location.href = '/';
                  }}
                  className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
                >
                  ← Back to overview
                </button>
              </div>
            </div>
          )}

          {/* Progress Display for Running Searches */}
          {currentSearch &&
            currentSearchStatus === 'running' &&
            progressData && (
              <div className="mt-4 bg-slate-800/30 backdrop-blur-sm p-4 rounded-xl border border-slate-700/50">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium text-white">
                      Step {progressData.current_step} of{' '}
                      {progressData.total_steps}: {progressData.step_name}
                    </span>
                  </div>
                  <span className="text-xs text-gray-400">
                    {progressData.overall_progress}% complete
                  </span>
                </div>

                {/* Overall Progress Bar */}
                <div className="mb-3">
                  <div className="w-full bg-slate-700 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-cyan-400 to-blue-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${progressData.overall_progress}%` }}
                    ></div>
                  </div>
                </div>

                {/* Current Step Progress */}
                {progressData.step_progress !== undefined && (
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-300">
                        {progressData.step_name}
                      </span>
                      <span className="text-xs text-gray-400">
                        {progressData.processed_count || 0} /{' '}
                        {progressData.total_count || 0}
                      </span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-1.5">
                      <div
                        className="bg-gradient-to-r from-purple-400 to-pink-500 h-1.5 rounded-full transition-all duration-300"
                        style={{ width: `${progressData.step_progress}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {/* Statistics */}
                {(progressData.total_harvested ||
                  progressData.total_analyzed ||
                  progressData.total_processed) && (
                  <div className="flex items-center space-x-6 pt-2 border-t border-slate-700/50">
                    {progressData.total_harvested && (
                      <div className="text-xs text-gray-400">
                        <span className="text-cyan-400 font-medium">
                          {progressData.total_harvested}
                        </span>{' '}
                        harvested
                      </div>
                    )}
                    {progressData.total_analyzed && (
                      <div className="text-xs text-gray-400">
                        <span className="text-green-400 font-medium">
                          {progressData.total_analyzed}
                        </span>{' '}
                        analyzed
                      </div>
                    )}
                    {progressData.total_processed && (
                      <div className="text-xs text-gray-400">
                        <span className="text-blue-400 font-medium">
                          {progressData.total_processed}
                        </span>{' '}
                        processed
                      </div>
                    )}
                  </div>
                )}

                {/* Status Message */}
                {statusMessage && (
                  <div className="mt-2 text-xs text-gray-300 bg-slate-700/30 rounded-lg px-3 py-1">
                    {statusMessage}
                  </div>
                )}
              </div>
            )}
        </div>

        {/* Content */}
        {showNewSearch && (
          <div className="mb-6">
            <SearchForm onSubmit={handleNewSearch} />
          </div>
        )}

        {showingQuickLookup && (
          <div className="bg-slate-800/30 backdrop-blur-sm rounded-xl border border-slate-700/50 shadow-2xl p-8">
            <QuickLookup {...quickLookupProps} />
          </div>
        )}

        {showingQuickLookupResult && (
          <div className="bg-slate-800/30 backdrop-blur-sm rounded-xl border border-slate-700/50 shadow-2xl">
            <QuickLookupResult {...quickLookupResultProps} />
          </div>
        )}

        {showingAllKeywords && (
          <KeywordTable
            keywords={keywords}
            search={null}
            showingAllKeywords={true}
            allKeywords={allKeywords}
            pagination={pagination}
          />
        )}

        {currentSearch && (
          <KeywordTable
            keywords={keywords}
            search={{
              ...currentSearch,
              status: currentSearchStatus || currentSearch.status,
            }}
            showingAllKeywords={false}
            allKeywords={allKeywords}
            pagination={pagination}
          />
        )}

        {!showingAllKeywords &&
          !currentSearch &&
          !showingQuickLookup &&
          !showingQuickLookupResult &&
          renderSearchesOverview()}
      </div>
    </div>
  );
}

// Keep track of the root to prevent multiple createRoot calls
let dashboardRoot = null;

// Auto-mount if the element exists
function mountDashboard() {
  const el = document.getElementById('dashboard-root');
  if (el) {
    const props = window.dashboardProps || {};

    if (dashboardRoot) {
      // Reuse existing root
      dashboardRoot.render(<Dashboard {...props} />);
    } else {
      // Create new root
      dashboardRoot = ReactDOM.createRoot(el);
      dashboardRoot.render(<Dashboard {...props} />);
    }
  }
}

// Clean up on page unload
function cleanupDashboard() {
  if (dashboardRoot) {
    dashboardRoot.unmount();
    dashboardRoot = null;
  }
}

// Mount on initial load
mountDashboard();

// Mount on Turbo navigation
document.addEventListener('turbo:load', mountDashboard);

// Cleanup on navigation away
document.addEventListener('turbo:before-visit', cleanupDashboard);

export default Dashboard;
